import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { useFinanceStore } from './financeStore.js';
import { useUserStore } from './userStore.js';
import { useMerchantStore } from './merchantStore.js';
import { matchMerchant } from '../lib/merchantMatcher.js';
import { isMerchantMemoryEnabled } from '../lib/tiers.js';
import { todayStr } from '../lib/calculations.js';
import { sSet } from '../lib/storage.js';

/* ══════════════════════════════════════════════════════
   BUSINESS STORE — Clients, revenue, expenses, P&L + allocation engine

   Data shapes:
   - Client:      { id, name, service, rate, color, phone }
   - BizIncome:   { id, clientId, clientName, service, date, amount, note,
                    allocation?: { owner, tax, opex, profit } }   ← new in v2
   - BizExpense:  { id, date, category, amount, note, merchantName?, merchantStatus?, splits?, confirmedAt?, deductible? }
                   merchantStatus: 'manual' | 'auto' | 'suggested' | 'unmatched' | 'confirmed'
   - Appointment: { id, clientId, clientName, service, date, time, duration, amount, note, paid }
   - ServiceCost: { id, name, productCost, duration, overheadPerHour, margin, minPrice, effectiveHourly, createdAt }

   Dependency direction:
     businessStore → financeStore, userStore   (via .getState(), non-reactive)
     businessStore → NOTHING imports businessStore back-door
   Keep it that way or allocate() will hit a circular import.
   ══════════════════════════════════════════════════════ */

const BIZ_CATS = [
  { id: 'supplies',   label: 'Supplies',   icon: 'supplies', color: 'var(--gold)' },
  { id: 'rent',       label: 'Rent/Space', icon: 'rent', color: '#8FA8C8' },
  { id: 'equipment',  label: 'Equipment',  icon: 'equipment', color: '#8FC89A' },
  { id: 'software',   label: 'Software',   icon: 'software', color: '#8FC8C0' },
  { id: 'marketing',  label: 'Marketing',  icon: 'marketing', color: '#A89FC8' },
  { id: 'insurance',  label: 'Insurance',  icon: 'insurance', color: '#C8A08F' },
  { id: 'education',  label: 'Education',  icon: 'education', color: '#E8C97A' },
  { id: 'taxes',      label: 'Taxes',      icon: 'taxes', color: '#9B9B9B' },
  { id: 'biz_other',  label: 'Other',      icon: 'biz_other', color: '#909090' },
];

const CLIENT_COLORS = ['#C9A96E','#8FA8C8','#8FC89A','#C87A7A','#A89FC8','#C8A08F','#8FC8C0','#E8C97A'];

const businessStorage = createJSONStorage(() => ({
  getItem: (name) => {
    try {
      const raw = localStorage.getItem(name);
      if (raw === '[object Object]') {
        localStorage.removeItem(name);
        return null;
      }
      return raw;
    } catch {
      return null;
    }
  },
  setItem: (name, value) => {
    try {
      localStorage.setItem(name, value);
    } catch {
      // The in-memory store remains usable when browser storage is unavailable.
    }
    // WHY: Business setup belongs to the signed-in account, not one device.
    // Send the exact Zustand envelope through the same account-scoped KV
    // boundary as personal money so logout, another browser, or a second
    // account cannot erase or inherit it. Account-isolation guards falsify it.
    try {
      void sSet(name, JSON.parse(value));
    } catch {
      // The local copy remains available and the normal sync path can retry.
    }
  },
  removeItem: (name) => {
    try {
      localStorage.removeItem(name);
    } catch {
      // Browser storage is optional.
    }
  },
}));

export { BIZ_CATS, CLIENT_COLORS };

export function getEstimatedTaxPeriod(date = new Date()) {
  const value = date instanceof Date ? date : new Date(date);
  const year = value.getFullYear();
  const month = value.getMonth();
  const quarter = month <= 2 ? 1 : month <= 4 ? 2 : month <= 7 ? 3 : 4;
  const ranges = {
    1: { startMonth: 0, endMonth: 2, deadline: 'Apr 15' },
    2: { startMonth: 3, endMonth: 4, deadline: 'Jun 15' },
    3: { startMonth: 5, endMonth: 7, deadline: 'Sep 15' },
    4: { startMonth: 8, endMonth: 11, deadline: 'Jan 15' },
  };
  return { year, quarter, ...ranges[quarter] };
}

export const useBusinessStore = create(
  persist(
    (set, get) => ({
      bizCategories: BIZ_CATS,

      // ── Clients ──
      clients: [],
      addClient: (client) => set(state => ({ clients: [...state.clients, client] })),
      updateClient: (id, updates) => set(state => ({
        clients: state.clients.map(c => c.id === id ? { ...c, ...updates } : c),
      })),
      removeClient: (id) => set(state => ({
        clients: state.clients.filter(c => c.id !== id),
      })),
      setClients: (clients) => set({ clients }),

      // ── Business Income ──
      bizIncome: [],
      addBizIncome: (entry) => set(state => ({ bizIncome: [...state.bizIncome, entry] })),
      updateBizIncome: (id, updates) => set(state => ({
        bizIncome: state.bizIncome.map(i => i.id === id ? { ...i, ...updates } : i),
      })),
      removeBizIncome: (id) => set(state => ({
        bizIncome: state.bizIncome.filter(i => i.id !== id),
      })),
      importBizIncome: (entries) => set(state => ({
        bizIncome: [...state.bizIncome, ...entries],
      })),
      setBizIncome: (bizIncome) => set({ bizIncome }),

      // ── Business Expenses ──
      bizExpenses: [],
      addBizExpense: (expense) => {
        const enriched = { ...expense };
        const tierState = { userStore: useUserStore.getState() };

        if (isMerchantMemoryEnabled(tierState)) {
          try {
            const userState = useUserStore.getState();
            const profession = userState.professionType || null;
            const merchantState = useMerchantStore.getState();
            const match = matchMerchant(enriched.note || '', profession, merchantState);

            // Dismissed-merchant short-circuit: an ignored override surfaces
            // through the matcher as { source: 'override', confidence: 'none' }.
            // Treat the new expense as manual and skip enqueueReview so the
            // user isn't nagged about a merchant they explicitly dismissed.
            if (match.source === 'override' && match.confidence === 'none') {
              enriched.merchantStatus = 'manual';
              enriched.merchantName = null;
            } else {
              enriched.merchantName = match.merchantName;
              enriched.merchantStatus =
                match.confidence === 'high'   ? 'auto'
                : match.confidence === 'medium' ? 'suggested'
                : 'unmatched';

              if (enriched.merchantStatus === 'auto' && match.category) {
                enriched.category = match.category;
                if (typeof match.deductible === 'boolean') {
                  enriched.deductible = match.deductible;
                }
              }

              if (enriched.merchantStatus !== 'auto') {
                useMerchantStore.getState().enqueueReview(enriched, match);
              }
            }
          } catch (err) {
            console.warn('[businessStore] addBizExpense matcher error, falling back to unmatched:', err);
            enriched.merchantStatus = 'unmatched';
          }
        } else {
          enriched.merchantStatus = 'manual';
        }

        // Initialize splits to null (Task 13 hooks UI to populate)
        if (enriched.splits === undefined) enriched.splits = null;

        set(state => ({ bizExpenses: [...state.bizExpenses, enriched] }));
      },
      updateBizExpense: (id, updates) => set(state => ({
        bizExpenses: state.bizExpenses.map(e => e.id === id ? { ...e, ...updates } : e),
      })),
      removeBizExpense: (id) => set(state => ({
        bizExpenses: state.bizExpenses.filter(e => e.id !== id),
      })),
      setBizExpenses: (bizExpenses) => set({ bizExpenses }),

      /**
       * Replace an expense's splits[] array. Throws if splits don't reconcile
       * to the parent amount within $0.01.
       * @throws if expense not found OR splits don't reconcile.
       */
      applySplitsToExpense: (id, splits) => {
        const expense = get().bizExpenses.find(e => e.id === id);
        if (!expense) {
          throw new Error(`[businessStore] applySplitsToExpense: expense ${id} not found`);
        }
        const sum = (splits || []).reduce((acc, s) => acc + (Number(s.amount) || 0), 0);
        // $0.01 tolerance handles typical percent-rounding ($16.67+$16.67 vs $33.33).
        // Penny-scale expenses with many fan-out splits may exceed this; not a real workflow.
        if (Math.abs(sum - expense.amount) > 0.01) {
          throw new Error(
            `[businessStore] split rows ($${sum.toFixed(2)}) must sum to expense amount ($${expense.amount.toFixed(2)})`
          );
        }
        set(state => ({
          bizExpenses: state.bizExpenses.map(e => e.id === id ? { ...e, splits } : e),
        }));
      },

      /**
       * Mark an expense as confirmed (called after user confirms in review tab).
       * Updates merchantName/category/deductible from the edits.
       * No-op if id not found (idempotent — safe to call from React effects).
       */
      confirmExpenseFromReview: (id, edits) => {
        set(state => ({
          bizExpenses: state.bizExpenses.map(e => {
            if (e.id !== id) return e;
            return {
              ...e,
              merchantName: edits?.merchantName ?? e.merchantName,
              category: edits?.category ?? e.category,
              deductible: edits?.deductible ?? e.deductible,
              merchantStatus: 'confirmed',
              confirmedAt: new Date().toISOString(),
            };
          }),
        }));
      },

      // ── Appointments ──
      appointments: [],
      addAppointment: (appt) => set(state => ({ appointments: [...state.appointments, appt] })),
      updateAppointment: (id, updates) => set(state => ({
        appointments: state.appointments.map(a => a.id === id ? { ...a, ...updates } : a),
      })),
      removeAppointment: (id) => set(state => ({
        appointments: state.appointments.filter(a => a.id !== id),
      })),
      setAppointments: (appointments) => set({ appointments }),

      markAppointmentPaid: (id) => {
        const { appointments, logBizIncome } = get();
        const appt = appointments.find(a => a.id === id);
        if (!appt || appt.paid) return null;
        set({
          appointments: appointments.map(a => a.id === id ? { ...a, paid: true } : a),
        });
        return logBizIncome({
          clientId: appt.clientId,
          clientName: appt.clientName,
          service: appt.service,
          date: appt.date,
          amount: appt.amount,
          note: 'Appointment payment',
        });
      },

      // ── Service Cost Calculator ──
      serviceCosts: [],
      addServiceCost: (sc) => set(state => ({ serviceCosts: [...state.serviceCosts, sc] })),
      updateServiceCost: (id, updates) => set(state => ({
        serviceCosts: state.serviceCosts.map(s => s.id === id ? { ...s, ...updates } : s),
      })),
      removeServiceCost: (id) => set(state => ({
        serviceCosts: state.serviceCosts.filter(s => s.id !== id),
      })),
      setServiceCosts: (serviceCosts) => set({ serviceCosts }),

      // ── Business Settings (Phase 0.3) ──
      // Single source of truth for the setup wizard + allocation engine.
      // Every new feature (pricing reality check, tax tracker, pace card,
      // owner draw, profit first) reads from this object.
      bizSettings: {
        // Picker-set: which mode this user is in. null means not yet decided.
        fit: null,
        // Picker-set: archetype slug (e.g. 'tattoo', 'hair'). null for new users.
        archetype: null,

        // Wizard Step 1 — Monthly expenses (full mode). Used by getPricingGap.
        expenses: {
          rent: 0, supplies: 0, tools: 0, software: 0,
          insurance: 0, marketing: 0, education: 0, other: 0,
        },
        // Wizard Step 1 — Monthly expenses (minimal mode).
        // Separate shape so mode switches don't stomp each other's values.
        expensesMinimal: {
          rent: 0, inventory: 0, payroll: 0, tools: 0, software: 0,
          insurance: 0, marketing: 0, education: 0, other: 0,
        },
        // Wizard Step 2 — Schedule + income goal (full mode only)
        schedule: {
          daysPerWeek: 5,
          clientsPerDay: 6,
          hoursPerDay: 8,
          weeksPerMonth: 4.3,
        },
        takeHomeGoal: 4000,
        targetMargin: 20,
        monthlyRevGoal: 7000,
        // Wizard Step 3 — Tax + Profit First
        // combinedPct is non-null when the user opted into the
        // "my tax situation is different" manual override, or when
        // minimal-mode wizard ran (which only asks for a single rate).
        tax: { fedPct: 15.3, statePct: 5, combinedPct: null, enabled: true },
        profitFirst: {
          enabled: false,
          ownerPct: 50, taxPct: 15, opexPct: 30, profitPct: 5,
        },
        ownerDraw: { enabled: false, bufferTarget: 5000 },
        // Wizard completion flag (gates the BizSetupWizard modal)
        wizardCompleted: false,
        wizardCompletedAt: null,
      },
      setBizSettings: (updates) => set(state => ({
        bizSettings: { ...state.bizSettings, ...updates },
      })),
      setBizSettingsField: (path, value) => set(state => {
        // Dot-path setter. Example: setBizSettingsField('tax.fedPct', 18).
        const keys = path.split('.');
        const next = JSON.parse(JSON.stringify(state.bizSettings));
        let cur = next;
        for (let i = 0; i < keys.length - 1; i++) {
          if (cur[keys[i]] == null) cur[keys[i]] = {};
          cur = cur[keys[i]];
        }
        cur[keys[keys.length - 1]] = value;
        return { bizSettings: next };
      }),

      // ── Allocation Engine (Phase 0.4) ──
      // Every paid appointment or manual income entry funnels through
      // logBizIncome → allocate → optional bucket deposits. This is the
      // single pipeline that makes the Business tab a closed loop.
      allocate: (amount) => {
        const amt = parseFloat(amount) || 0;
        const { bizSettings } = get();
        const pf = bizSettings.profitFirst;
        const tax = bizSettings.tax;

        // Profit First takes precedence when enabled (4-bucket split).
        if (pf.enabled) {
          return {
            owner:  amt * (pf.ownerPct  / 100),
            tax:    amt * (pf.taxPct    / 100),
            opex:   amt * (pf.opexPct   / 100),
            profit: amt * (pf.profitPct / 100),
          };
        }
        // Tax-only mode: everything not taxed is owner pay.
        if (tax.enabled) {
          // combinedPct (set by minimal-mode wizard or the "my tax
          // situation is different" disclosure in full-mode Step 3)
          // overrides the fed+state split when present.
          const combinedPct = tax.combinedPct != null
            ? tax.combinedPct / 100
            : (tax.fedPct + tax.statePct) / 100;
          return {
            owner:  amt * (1 - combinedPct),
            tax:    amt * combinedPct,
            opex:   0,
            profit: 0,
          };
        }
        // No allocation configured — everything is owner pay.
        return { owner: amt, tax: 0, opex: 0, profit: 0 };
      },

      // Single entry point for all business income logging.
      // Returns the allocation split so the UI can render a toast.
      logBizIncome: (entry) => {
        const amount = parseFloat(entry.amount) || 0;
        const split = get().allocate(amount);
        const record = {
          id: entry.id || 'bi' + Date.now(),
          ...entry,
          amount,
          allocation: split,
        };
        set(state => ({ bizIncome: [...state.bizIncome, record] }));

        // ── Bridge: route slices to personal-side containers ──
        // Tax slice → existing financeStore.taxReserve (reuses existing
        // quarterly-payment UI at Planning/TaxReserveTab).
        // Profit/OpEx slices → user-selected savings buckets via the
        // userStore.bizBridge config.
        try {
          const bridge = useUserStore.getState().bizBridge;
          if (bridge && bridge.enabled) {
            const fin = useFinanceStore.getState();
            if (split.tax > 0 && typeof fin.addTaxReserve === 'function') {
              fin.addTaxReserve(
                split.tax,
                `Biz allocation: ${entry.clientName || 'payment'}`
              );
            }
            if (bridge.profitBucketId && split.profit > 0 && typeof fin.depositToBucket === 'function') {
              fin.depositToBucket(bridge.profitBucketId, split.profit, 'Biz allocation');
            }
            if (bridge.opexBucketId && split.opex > 0 && typeof fin.depositToBucket === 'function') {
              fin.depositToBucket(bridge.opexBucketId, split.opex, 'Biz allocation');
            }
          }
        } catch {
          // Bridge failure must never break income logging itself.
          // The record is already saved; downstream bucket writes are
          // best-effort.
        }

        return split;
      },

      // Mirror of logBizIncome on the payment side: single entry point for
      // quarterly tax payments so the personal→business bridge can close the
      // loop. Always debits the dedicated financeStore.taxReserve; only
      // appends a bizExpense row when the bridge is on AND the user has
      // opted in via bizBridge.autoLogTaxPayment (default on). Gated by
      // .enabled because a sole-prop user who hasn't enabled the bridge
      // should NOT see their tax payment auto-distort P&L.
      logTaxPayment: (amount, note, targetPeriod = null) => {
        const amt = parseFloat(amount) || 0;
        if (amt <= 0) return { withdrawn: 0, logged: false };

        const fin = useFinanceStore.getState();
        let withdrawn = 0;
        if (typeof fin.withdrawTaxReserve === 'function') {
          withdrawn = fin.withdrawTaxReserve(amt, note, targetPeriod);
        }
        if (withdrawn <= 0) return { withdrawn: 0, logged: false };

        let logged = false;
        try {
          const bridge = useUserStore.getState().bizBridge;
          if (bridge?.enabled && bridge?.autoLogTaxPayment) {
            set(state => ({
              bizExpenses: [...state.bizExpenses, {
                id: 'be' + Date.now().toString(36),
                date: todayStr(),
                category: 'taxes',
                amount: withdrawn,
                note: note || 'Quarterly payment',
                taxPayment: true,
                ...(targetPeriod?.quarter && targetPeriod?.year && {
                  targetQuarter: Number(targetPeriod.quarter),
                  targetYear: Number(targetPeriod.year),
                }),
              }],
            }));
            logged = true;
          }
        } catch {
          // Bridge failure must never break the withdrawal itself.
          // The reserve debit is already persisted.
        }

        return { withdrawn, logged };
      },

      // ── Computed Helpers ──
      getMonthRevenue: (monthKey) => {
        return get().bizIncome
          .filter(i => i.date?.startsWith(monthKey))
          .reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
      },
      getMonthExpenses: (monthKey) => {
        return get().bizExpenses
          // Sole-proprietor estimated-tax payments are personal tax payments,
          // not Schedule C operating expenses.
          .filter(e => e.date?.startsWith(monthKey) && e.taxPayment !== true)
          .reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);
      },
      getMonthProfit: (monthKey) => {
        const rev = get().getMonthRevenue(monthKey);
        const exp = get().getMonthExpenses(monthKey);
        return rev - exp;
      },
      getClientRevenue: (clientId, monthKey) => {
        return get().bizIncome
          .filter(i => i.clientId === clientId && i.date?.startsWith(monthKey))
          .reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
      },

      // ── Dashboard Card Getters (Phase 0.6) ──
      // Pace card: current month revenue vs goal + projection
      getMonthPace: (monthKey) => {
        const { bizSettings, getMonthRevenue } = get();
        const actual = getMonthRevenue(monthKey);
        const goal = bizSettings.monthlyRevGoal || 0;
        const now = new Date();
        const [yStr, mStr] = (monthKey || '').split('-');
        const y = parseInt(yStr, 10);
        const m = parseInt(mStr, 10);
        if (!y || !m) return { goal, actual, projected: 0, daysLeft: 0, dayOfMonth: 0, daysInMonth: 0, status: 'unknown' };
        const daysInMonth = new Date(y, m, 0).getDate();
        const isCurrentMonth = y === now.getFullYear() && m === now.getMonth() + 1;
        const selectedStart = new Date(y, m - 1, 1);
        const currentStart = new Date(now.getFullYear(), now.getMonth(), 1);
        const isFutureMonth = selectedStart > currentStart;
        const isPastMonth = selectedStart < currentStart;
        const dayOfMonth = isCurrentMonth ? now.getDate() : isPastMonth ? daysInMonth : 0;
        const daysLeft = isFutureMonth ? daysInMonth : Math.max(0, daysInMonth - dayOfMonth);
        const projected = isCurrentMonth && dayOfMonth > 0
          ? (actual / dayOfMonth) * daysInMonth
          : actual;
        const expectedByNow = goal * (dayOfMonth / daysInMonth);
        const status = isFutureMonth
          ? 'upcoming'
          : goal <= 0
          ? 'unknown'
          : isPastMonth
            ? (actual >= goal ? 'complete' : 'missed')
            : (actual >= expectedByNow
              ? 'ontrack'
              : (actual >= expectedByNow * 0.8 ? 'slightly-behind' : 'behind'));
        return {
          goal,
          actual,
          projected,
          daysLeft,
          dayOfMonth,
          daysInMonth,
          status,
          monthState: isFutureMonth ? 'future' : isPastMonth ? 'past' : 'current',
        };
      },

      // Tax bucket card: IRS estimated-tax periods are not calendar quarters.
      // Q2 is Apr-May; Q3 is Jun-Aug; Q4 is Sep-Dec.
      getQuarterTaxOwed: (date = new Date()) => {
        const period = getEstimatedTaxPeriod(date);
        const setAside = get().bizIncome
          .filter(i => {
            if (!i.date) return false;
            const d = new Date(`${i.date}T00:00:00`);
            return Number.isFinite(d.getTime())
              && d.getFullYear() === period.year
              && d.getMonth() >= period.startMonth
              && d.getMonth() <= period.endMonth;
          })
          .reduce((s, i) => s + (parseFloat(i.allocation?.tax) || 0), 0);
        return {
          quarter: period.quarter,
          year: period.year,
          setAside,
          nextDeadline: period.deadline,
        };
      },

      // Owner draw card: average recorded owner allocations over recent
      // completed months; when none exist for a month, use actual net profit.
      getSmoothedOwnerDraw: () => {
        const now = new Date();
        const firstMonth = new Date(now.getFullYear(), now.getMonth() - 12, 1);
        const currentMonthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
        const months = {};
        get().bizIncome.forEach(i => {
          if (!i.date) return;
          const d = new Date(`${i.date}T00:00:00`);
          if (!Number.isFinite(d.getTime()) || d < firstMonth || i.date.startsWith(currentMonthKey)) return;
          const key = i.date.slice(0, 7);
          if (!months[key]) months[key] = { revenue: 0, expenses: 0, owner: 0, hasOwnerAllocation: false };
          months[key].revenue += parseFloat(i.amount) || 0;
          if (i.allocation?.owner != null) {
            months[key].owner += parseFloat(i.allocation.owner) || 0;
            months[key].hasOwnerAllocation = true;
          }
        });
        get().bizExpenses.forEach(e => {
          if (!e.date || e.taxPayment === true) return;
          const d = new Date(`${e.date}T00:00:00`);
          if (!Number.isFinite(d.getTime()) || d < firstMonth || e.date.startsWith(currentMonthKey)) return;
          const key = e.date.slice(0, 7);
          if (!months[key]) months[key] = { revenue: 0, expenses: 0, owner: 0, hasOwnerAllocation: false };
          months[key].expenses += parseFloat(e.amount) || 0;
        });
        const rows = Object.values(months);
        const values = rows.map(row => (
          row.hasOwnerAllocation
            ? Math.max(0, row.owner)
            : Math.max(0, row.revenue - row.expenses)
        ));
        if (!values.length) return { smoothed: 0, best: 0, worst: 0, months: 0, basis: 'none' };
        const avg = values.reduce((a, b) => a + b, 0) / values.length;
        return {
          smoothed: avg,
          best: Math.max(...values),
          worst: Math.min(...values),
          months: values.length,
          basis: rows.some(row => row.hasOwnerAllocation)
            ? 'recorded owner allocations, with net-profit fallback'
            : 'net profit after operating expenses',
        };
      },

      // Pricing gap card: actual avg/client vs minimum needed
      getPricingGap: (monthKey) => {
        const { bizSettings, bizIncome } = get();
        // Default-empty so a partial bizSettings (mid-wizard, cross-version
        // migration, legacy seeds) doesn't crash the dashboard.
        const {
          expenses = {},
          schedule = {},
          takeHomeGoal = 0,
          targetMargin = 0,
        } = bizSettings || {};
        const totalExp = Object.values(expenses).reduce((a, b) => a + (parseFloat(b) || 0), 0);
        const monthlyClients = Math.round(
          (schedule.clientsPerDay || 0) * (schedule.daysPerWeek || 0) * (schedule.weeksPerMonth || 0)
        );
        const needed = totalExp + (takeHomeGoal || 0);
        const minPerClient = monthlyClients > 0 ? needed / monthlyClients : 0;
        const marginRate = Number(targetMargin) / 100;
        const marginValid = Number.isFinite(marginRate) && marginRate >= 0 && marginRate < 1;
        const recPerClient = marginValid && minPerClient > 0
          ? minPerClient / (1 - marginRate)
          : 0;

        const monthEntries = (bizIncome || []).filter(i => i.date?.startsWith(monthKey));
        const actualAvg = monthEntries.length > 0
          ? monthEntries.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0) / monthEntries.length
          : 0;

        const gap = recPerClient - actualAvg;
        const annualLoss = gap > 0 ? gap * monthlyClients * 12 : 0;

        return {
          actualAvg, minPerClient, recPerClient,
          gap, annualLoss, monthlyClients, needed, totalExp,
          targetMargin: Number(targetMargin) || 0,
          marginValid,
        };
      },
    }),
    {
      name: 'karat-business',
      storage: businessStorage,
      version: 2,
      partialize: (state) => ({
        clients: state.clients,
        bizIncome: state.bizIncome,
        bizExpenses: state.bizExpenses,
        appointments: state.appointments,
        serviceCosts: state.serviceCosts,
        bizSettings: state.bizSettings,
      }),
      // Version 1 → 2 migration: existing Business Mode users (who
      // completed the old wizard) are backfilled into full mode so they
      // never unexpectedly see the new archetype picker. Users who never
      // finished the wizard will see the picker on their next attempt.
      migrate: (persistedState, fromVersion) => {
        if (!persistedState) return persistedState;
        const next = { ...persistedState };
        // v1 → v2: backfill archetype picker fields. Existing users who
        // completed the old wizard are silently routed into full mode
        // so they don't see the new picker unexpectedly.
        if (fromVersion < 2) {
          const bs = next.bizSettings ? { ...next.bizSettings } : null;
          if (bs) {
            if (bs.wizardCompleted && !bs.fit) {
              bs.fit = 'full';
            }
            if (bs.archetype === undefined) bs.archetype = null;
            if (!bs.expensesMinimal) {
              bs.expensesMinimal = {
                rent: 0, inventory: 0, payroll: 0, tools: 0, software: 0,
                insurance: 0, marketing: 0, education: 0, other: 0,
              };
            }
            if (bs.tax && bs.tax.combinedPct === undefined) {
              bs.tax = { ...bs.tax, combinedPct: null };
            }
            next.bizSettings = bs;
          }
        }
        return next;
      },
    }
  )
);
